/**
 * Planner pair offer: page behaviour.
 *
 * - Colour swatch -> thumbnail + variant id on the row's add button.
 * - Add to bag -> POST /cart/add.js, then dispatch the theme's `variant:add` and
 *   `cart:refresh` events so the Impact cart drawer re-renders and opens.
 * - Sticky offer bar reads /cart.js (real automatic-discount allocations) and
 *   shows odd/even pair messaging. Display only; checkout enforces the offer.
 * - Smooth-scrolls in-page anchors with a sticky-header offset.
 *
 * No dependencies. Scoped to [data-pair-offer].
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-pair-offer]');
  if (!root) return;

  const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const strings = readJson(root.querySelector('[data-pair-strings]')) || {};
  const pct = Number(root.dataset.offerPercent || strings.pct || 25);
  const eligibleIds = new Set(
    (root.dataset.eligibleIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
  );
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';

  /* ---------------------------------------------------------------- helpers */

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('[pair-offer] bad JSON block', err);
      return null;
    }
  }

  /** Format cents using the shop's money format (handles {{amount}} and {{amount_no_decimals}}). */
  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const noDecimals = /amount_no_decimals/.test(moneyFormat);
    // Whole dollars (rounded) when the shop format hides cents, matching the cart drawer.
    const value = noDecimals ? Math.round(amount).toLocaleString() : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return moneyFormat.replace(/\{\{\s*amount(_no_decimals)?(_with_comma_separator)?\s*\}\}/, value).replace(/<[^>]+>/g, '');
  }

  /** Whole-dollar money, rounded like the theme's {{amount_no_decimals}} cart display. */
  function formatWholeMoney(cents) {
    const value = Math.round((Number(cents) || 0) / 100).toLocaleString();
    return moneyFormat.replace(/\{\{\s*amount(_no_decimals)?(_with_comma_separator)?\s*\}\}/, value).replace(/<[^>]+>/g, '');
  }

  function stickyOffset() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height');
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed + 16 : 80;
  }

  async function fetchCart() {
    const res = await fetch(routes + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart.js ' + res.status);
    return res.json();
  }

  function openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      drawer.show();
      return;
    }
    const trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) trigger.click();
  }

  /* ------------------------------------------------------------- swatches */

  root.querySelectorAll('[data-pair-product]').forEach((row) => {
    const thumb = row.querySelector('.pair-offer__thumb-img');
    const addBtn = row.querySelector('[data-pair-add]');
    const nameEl = row.querySelector('[data-pair-colour-name]');

    row.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.matches('[data-pair-swatch]')) return;

      const available = input.dataset.available === 'true';
      if (addBtn) {
        addBtn.dataset.variantId = input.dataset.variantId || '';
        addBtn.disabled = !available;
      }
      if (nameEl) nameEl.textContent = input.value + (available ? '' : ' (sold out)');
      if (thumb && input.dataset.image) {
        thumb.src = input.dataset.image;
        thumb.removeAttribute('srcset');
      }
    });
  });

  /* Swatch strip (phones): fade only the edges that still have swatches hidden behind them. */
  root.querySelectorAll('.pair-offer__swatches').forEach((strip) => {
    const update = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      strip.classList.toggle('has-more-start', max > 1 && strip.scrollLeft > 1);
      strip.classList.toggle('has-more-end', max > 1 && strip.scrollLeft < max - 1);
    };
    strip.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  /* ----------------------------------------------------------- add to bag */

  root.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-pair-add]');
    if (!btn || btn.disabled) return;

    const variantId = Number(btn.dataset.variantId);
    if (!variantId) return;

    const label = btn.querySelector('[data-pair-add-label]') || btn;
    const idle = strings.add || label.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    label.textContent = strings.adding || 'Adding…';

    try {
      const res = await fetch(routes + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'add failed');

      const cart = await fetchCart();
      document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: data.items || [data], cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      label.textContent = strings.added || 'Added';
      updateBar(cart);
      openCartDrawer();
    } catch (err) {
      console.error('[pair-offer] add to bag failed', err);
      label.textContent = strings.error || 'Could not add. Try again.';
    } finally {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      window.setTimeout(() => { label.textContent = idle; }, 2200);
    }
  });

  /* ------------------------------------------------------------ sticky bar */

  const bar = root.querySelector('[data-pair-bar]');
  const barText = root.querySelector('[data-pair-bar-text]');
  const barLead = root.querySelector('[data-pair-bar-lead]');
  const barTotal = root.querySelector('[data-pair-bar-total]');
  const barShort = root.querySelector('[data-pair-bar-short]');
  const pip1 = root.querySelector('[data-pair-pip="1"]');
  const pip2 = root.querySelector('[data-pair-pip="2"]');

  function updateBar(cart) {
    if (!bar) return;
    const items = Array.isArray(cart.items) ? cart.items : [];
    let eligibleCount = 0;
    let savings = 0;
    items.forEach((item) => {
      if (!eligibleIds.has(Number(item.product_id))) return;
      eligibleCount += Number(item.quantity) || 0;
      savings += Number(item.line_level_total_discount) || 0;
    });

    if (eligibleCount === 0) {
      bar.hidden = true;
      root.classList.remove('has-bar');
      return;
    }

    const odd = eligibleCount % 2 === 1;
    let template = odd ? (strings.addOne || '') : (strings.pairComplete || '');
    // Even count but no allocation in the cart (e.g. buyer not eligible yet): neutral copy.
    if (!odd && savings === 0 && strings.pairNoSavings) template = strings.pairNoSavings;
    barText.textContent = template.replace('{savings}', formatMoney(savings)).replace('{pct}', String(pct));
    // Whole dollars, rounded the same way the theme's cart drawer shows them; when a discount is in
    // the cart, show the original total crossed out next to what they'll pay.
    const paying = formatWholeMoney(cart.total_price);
    const original = Number(cart.original_total_price) || 0;
    barTotal.textContent = '';
    if (original > Number(cart.total_price)) {
      const was = document.createElement('s');
      was.className = 'pair-offer-bar__was';
      was.textContent = formatWholeMoney(original);
      barTotal.append(was, ' ');
    }
    const now = document.createElement('strong');
    now.textContent = paying;
    barTotal.append(now);
    if (barLead) {
      const lead = odd ? (strings.leadOdd || '') : (strings.leadEven || '');
      barLead.textContent = lead.replace('{n}', String(eligibleCount)).replace('{next}', String(eligibleCount + 1));
    }
    if (barShort) {
      let short = odd ? (strings.shortOdd || '') : (strings.shortEven || '');
      if (!odd && savings === 0 && strings.shortNoSavings) short = strings.shortNoSavings;
      barShort.textContent = short.replace('{n}', String(eligibleCount)).replace('{next}', String(eligibleCount + 1)).replace('{pct}', String(pct));
    }
    setLit(pip1, eligibleCount >= 1);
    setLit(pip2, !odd && eligibleCount >= 2);
    bar.hidden = false;
    root.classList.add('has-bar');
  }

  /** Light a planner icon; give it a small pop only when it newly fills. */
  function setLit(icon, lit) {
    if (!icon) return;
    const was = icon.classList.contains('is-lit');
    icon.classList.toggle('is-lit', lit);
    if (lit && !was) {
      icon.classList.remove('is-pop');
      void icon.getBoundingClientRect();
      icon.classList.add('is-pop');
    }
  }

  async function refreshBar() {
    try {
      updateBar(await fetchCart());
    } catch (err) {
      console.warn('[pair-offer] cart refresh failed', err);
    }
  }

  document.addEventListener('cart:refresh', refreshBar);
  document.addEventListener('cart:change', refreshBar);
  refreshBar();

  /* --------------------------------------------------------- smooth scroll */

  root.querySelectorAll('a[href^="#pair-offer-"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - stickyOffset();
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
})();

/**
 * Planner pair offer: full-spread viewer.
 *
 * - [data-pair-zoom] buttons open a native <dialog> with the full two-page spread.
 * - The large image is only fetched on intent (hover / touch / focus) or open, so it
 *   never competes with the initial page load.
 * - Tap / click the page to toggle between "fit" and "zoomed" (pans by scrolling);
 *   pinch-zoom still works. Esc, the close button or a backdrop click closes it.
 */
(function () {
  'use strict';

  const viewer = document.querySelector('[data-pair-viewer]');
  if (!viewer || typeof viewer.showModal !== 'function') return;

  const img = viewer.querySelector('[data-pair-viewer-img]');
  const stage = viewer.querySelector('[data-pair-viewer-stage]');
  const caption = viewer.querySelector('[data-pair-viewer-caption]');
  const closeBtn = viewer.querySelector('[data-pair-viewer-close]');
  const warmed = new Set();

  function warm(url) {
    if (!url || warmed.has(url)) return;
    warmed.add(url);
    const pre = new Image();
    pre.decoding = 'async';
    pre.src = url;
  }

  function setZoom(on, clientX, clientY) {
    if (on === stage.classList.contains('is-zoomed')) return;
    // Remember where the tap landed (as a fraction of the image) so we zoom into that spot.
    const rect = img.getBoundingClientRect();
    const fx = rect.width ? (clientX - rect.left) / rect.width : 0.5;
    const fy = rect.height ? (clientY - rect.top) / rect.height : 0.5;
    stage.classList.toggle('is-zoomed', on);
    if (on) {
      requestAnimationFrame(() => {
        stage.scrollLeft = fx * img.offsetWidth - stage.clientWidth / 2;
        stage.scrollTop = fy * img.offsetHeight - stage.clientHeight / 2;
      });
    }
  }

  function open(trigger) {
    const url = trigger.dataset.full;
    if (!url) return;
    warm(url);
    stage.classList.remove('is-zoomed');
    img.src = url;
    img.alt = trigger.dataset.caption || '';
    caption.textContent = trigger.dataset.caption || '';
    viewer.showModal();
    document.documentElement.classList.add('pair-offer-viewer-open');
    closeBtn.focus({ preventScroll: true });
  }

  document.querySelectorAll('[data-pair-zoom]').forEach((trigger) => {
    const intent = () => warm(trigger.dataset.full);
    trigger.addEventListener('pointerenter', intent, { passive: true });
    trigger.addEventListener('touchstart', intent, { passive: true });
    trigger.addEventListener('focus', intent);
    trigger.addEventListener('click', () => open(trigger));
  });

  img.addEventListener('click', (event) => {
    setZoom(!stage.classList.contains('is-zoomed'), event.clientX, event.clientY);
  });

  closeBtn.addEventListener('click', () => viewer.close());

  // Clicks on the dimmed backdrop (outside the stage content) close the viewer.
  viewer.addEventListener('click', (event) => {
    if (event.target === viewer || (event.target === stage && !stage.classList.contains('is-zoomed'))) viewer.close();
  });

  viewer.addEventListener('close', () => {
    document.documentElement.classList.remove('pair-offer-viewer-open');
    stage.classList.remove('is-zoomed');
  });
})();
